import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mic, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { startSubscriptionCheckout } from '@/utils/payments/subscribe';

const FONT = { fontFamily: "'Instrument Sans', system-ui, sans-serif" };

const FEATURES = [
  'Find hospitals and oncologists',
  'Cost calculator',
  'Second opinion',
  'Journey builder',
  'Rare cancers',
  'Teleconsultation',
  'Care marketplace',
  'Unlimited chat with Vani',
];

const PLANS = [
  {
    id: 'monthly',
    name: 'Monthly',
    price: '$19.99',
    period: '/mo',
    compare: '$39.99',
    note: 'Cancel anytime',
    cta: 'Continue · $19.99',
  },
  {
    id: 'yearly',
    name: 'Yearly',
    price: '$14.99',
    period: '/mo',
    compare: '$19.99',
    note: 'Billed $179.88 once a year · save 25%',
    sash: 'Save 25%',
    cta: 'Continue · $179.88/year',
  },
  {
    id: 'lifetime',
    name: 'Lifetime',
    price: '$999',
    period: ' once',
    compare: '$2,399',
    note: 'One payment. No renewals.',
    sash: 'Best value',
    featured: true,
    cta: 'Get lifetime access · $999',
  },
];

export default function ProPaywallModal({
  open,
  onClose,
  onUnlocked,
  returnPath,
  title = 'ByOnco PRO unlocks every feature',
  subtitle = 'Full access to oncology companion care, expert second opinions, hospital matching, and voice.',
}) {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);
  const [selected, setSelected] = useState('monthly');
  const chosen = PLANS.find((plan) => plan.id === selected) || PLANS[0];
  const nextPath = returnPath || (typeof window !== 'undefined' ? window.location.pathname + window.location.search : '/app');

  useEffect(() => {
    if (!open) {
      setBusy(false);
      setError('');
      setDone(null);
      setSelected('monthly');
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape' && !busy) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onClose]);

  if (!open) return null;

  const pay = async (type) => {
    if (!isAuthenticated) {
      onClose?.();
      navigate(`/authentication?redirect=${encodeURIComponent(nextPath)}`);
      return;
    }
    if (busy) return;
    setBusy(true);
    setError('');
    await startSubscriptionCheckout({
      planType: type,
      user,
      onSuccess: async ({ pending, entitlement } = {}) => {
        setBusy(false);
        setDone({ pending: !!pending, lifetime: type === 'lifetime' || entitlement?.lifetime });
        onUnlocked?.();
      },
      onDismiss: () => setBusy(false),
      onFail: (err) => {
        setBusy(false);
        setError(err.message || 'Payment did not complete. Please try again.');
      },
    });
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-forest-ink/40 p-4"
      style={FONT}
      role="dialog"
      aria-modal="true"
      aria-labelledby="pro-paywall-title"
      onClick={(event) => {
        if (event.target === event.currentTarget && !busy) onClose?.();
      }}
    >
      <div className="relative w-full max-w-2xl max-h-[calc(100vh-2rem)] overflow-y-auto rounded-3xl border border-hair bg-paper p-6 sm:p-8">
        <button
          type="button"
          onClick={() => !busy && onClose?.()}
          className="absolute top-4 right-4 text-[#3d4a42] hover:text-forest-ink"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        {done ? (
          <div className="text-center space-y-4 pt-2">
            <h2 id="pro-paywall-title" className="text-2xl font-semibold text-forest-ink">
              {done.pending ? 'Payment received' : 'You’re in'}
            </h2>
            <p className="text-[#3d4a42]">
              {done.pending
                ? 'Access is turning on now. Refresh in a few seconds if a feature still asks to upgrade.'
                : done.lifetime
                  ? 'Lifetime access is on. Every feature, including Voice, is yours.'
                  : 'ByOnco PRO is on. You now have access to every feature, including Voice.'}
            </p>
            <button
              type="button"
              className="w-full h-12 rounded-full bg-forest-ink text-cream-paper font-medium"
              onClick={() => {
                onClose?.();
                if (nextPath && nextPath !== window.location.pathname + window.location.search) {
                  navigate(nextPath);
                } else {
                  window.location.reload();
                }
              }}
            >
              Continue
            </button>
          </div>
        ) : (
          <>
            <h2 id="pro-paywall-title" className="text-2xl sm:text-3xl font-semibold text-forest-ink pr-8">
              {title}
            </h2>
            <p className="mt-2 text-[#3d4a42]">{subtitle}</p>

            <div className="mt-5 rounded-2xl border border-forest-ink bg-mist px-4 py-3 flex items-start gap-3">
              <Mic className="h-5 w-5 text-forest-ink mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-forest-ink">Voice is PRO only</p>
                <p className="text-sm text-[#3d4a42]">
                  Hold-to-talk and in-app voice are included with every ByOnco plan.
                </p>
              </div>
            </div>

            <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-forest-ink">
              {FEATURES.map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <span aria-hidden="true">✓</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3" role="radiogroup" aria-label="Choose a plan">
              {PLANS.map((plan) => (
                <button
                  key={plan.id}
                  type="button"
                  role="radio"
                  aria-checked={selected === plan.id}
                  onClick={() => setSelected(plan.id)}
                  className={`text-left rounded-2xl border p-4 ${
                    selected === plan.id ? 'border-forest-ink bg-mist' : 'border-hair bg-cream-paper'
                  }`}
                >
                  {plan.sash ? (
                    <span className="inline-block text-[10px] font-semibold uppercase tracking-wide text-forest-ink bg-paper border border-hair rounded-full px-2 py-0.5 mb-2">
                      {plan.sash}
                    </span>
                  ) : null}
                  <b className="block text-forest-ink">{plan.name}</b>
                  <div className="mt-1 text-forest-ink">
                    <s className="text-[#3d4a42] text-xs mr-1">{plan.compare}</s>
                    <strong>{plan.price}</strong>
                    <small>{plan.period}</small>
                  </div>
                  <p className="text-xs text-[#3d4a42] mt-1">{plan.note}</p>
                </button>
              ))}
            </div>

            {error ? <p className="mt-3 text-sm text-forest-ink" role="alert">{error}</p> : null}

            <button
              type="button"
              className="mt-5 w-full h-12 rounded-full bg-forest-ink text-cream-paper font-medium disabled:opacity-50"
              disabled={busy}
              onClick={() => pay(selected)}
            >
              {busy ? 'Opening secure checkout…' : chosen.cta}
            </button>
            <p className="mt-3 text-xs text-[#3d4a42] text-center">
              Card via Razorpay · Access on confirmation · Admins keep free access
            </p>
          </>
        )}
      </div>
    </div>
  );
}
